import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Button } from "reactstrap";

const TanstackTable = ({ data, columns }) => {
  const [globalFilter, setGlobalFilter] = React.useState("");
  const safeData = Array.isArray(data) ? data : [];

  const table = useReactTable({
    data: safeData,
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const filteredCount = table.getFilteredRowModel().rows.length;
  const totalCount = safeData.length;
  const currentPageRows = table.getRowModel().rows.length;
  const startRow = filteredCount === 0 ? 0 : pageIndex * pageSize + 1;
  const endRow = filteredCount === 0 ? 0 : startRow + currentPageRows - 1;
  const totalColumns = table.getVisibleLeafColumns().length || columns.length || 1;

  return (
    <>
      <div className="row g-2 align-items-center mb-3">
        <div className="col-sm-7 col-md-5 col-lg-4">
          <div className="input-group">
            <input
              type="search"
              className="form-control"
              placeholder="Search table..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <span className="input-group-text bg-transparent border-start-0">
              <i className="ri-search-line"></i>
            </span>
            {globalFilter ? (
              <button
                type="button"
                className="btn btn-light border"
                onClick={() => setGlobalFilter("")}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
        <div className="col-sm-5 col-md-7 col-lg-8">
          <div className="d-flex justify-content-sm-end align-items-center gap-2">
            <label className="mb-0 text-muted">Rows:</label>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 90 }}
              value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <Table className="table table-hover align-middle table-nowrap mb-0">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      cursor: header.column.getCanSort() ? "pointer" : "default",
                      whiteSpace: "nowrap",
                    }}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    <div className="d-flex align-items-center gap-1">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getCanSort() ? (
                        <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                          {header.column.getIsSorted() === "asc"
                            ? "▲"
                            : header.column.getIsSorted() === "desc"
                            ? "▼"
                            : "↕"}
                        </span>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} className="text-center text-muted py-4">
                  No records found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <span className="text-muted small">
          Showing <strong>{startRow}</strong> - <strong>{endRow}</strong> of{" "}
          <strong>{filteredCount}</strong> records
          {globalFilter ? (
            <>
              {" "}(filtered from <strong>{totalCount}</strong>)
            </>
          ) : null}
        </span>
        <div className="d-flex align-items-center gap-2">
          <Button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            color="secondary"
            size="sm"
          >
            Previous
          </Button>
          <span className="small text-muted">
            Page <strong>{pageIndex + 1}</strong> of {table.getPageCount() || 1}
          </span>
          <Button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            color="secondary"
            size="sm"
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

export default TanstackTable;
